/**
 * Рассказать расширению, что сейчас наверху ленты.
 *
 * Панель расширения умеет читать остатки на счетах, но не знает, чего им должно
 * хватать; лента знает суммы ног, но про счета человека не знает ничего — и
 * знать не должна. Здесь эти два знания соединяются, и соединяются в браузере:
 * остатки на наш сервер не уезжают.
 *
 * Отправляется редко. Лента перерисовывается по нескольку раз в секунду, а
 * панель читают глазами; слать состав на каждый кадр значило бы разбудить
 * служебный процесс расширения сотню раз в минуту ради одного и того же ответа.
 */

import { useEffect, useRef } from 'react';

import { apiUrl } from '../lib/build';
import { parseMisses, report, type AimMiss } from '../aim/miss';
import { fetchProfiles } from '../aim/profiles';
import { parseDrive } from '../confirm/drive';
import { profiles as tellProfiles, roster } from '../confirm/publish';
import { rosterOf } from '../confirm/roster';
import { parseSecs, report as reportSecs, type ReadySec } from '../confirm/secs';
import { parseShelf } from '../confirm/shelf';
import type { PageClip } from '../confirm/clip';
import { clipIds } from './clip';
import { rememberSite } from './liveQuote';
import { useScreen } from './store';
import { notePages } from '../lib/sight';
import { legsOf, forForm, urlOf } from './act';
import { useDrive } from './driveNote';
import type { FeedRow } from './rows';
import { bestWindow, useShift } from './shift';

/** Реже этого состав не пересылается. */
const EVERY_MS = 20_000;

export function useTell(rows: readonly FeedRow[]): void {
  const last = useRef(0);
  useEffect(() => {
    const now = Date.now();
    if (now - last.current < EVERY_MS) return;
    last.current = now;
    roster(
      rosterOf(
        rows.map((row) => ({
          id: row.id,
          profit: row.row.profit,
          legs: legsOf(row).map((leg) => ({
            source: leg.source,
            url: urlOf(row.id, leg),
            // Ровно та сумма, которую человек вводит в форму конторы: сравнивать
            // остаток с чем-то другим значит промахнуться на комиссию биржи или
            // на риск ноги «против».
            need: Number(forForm(leg)),
            cur: leg.cur === 'RUB' ? '₽' : leg.cur,
          })),
        })),
        // Часы прогрева — по карте ЭТОГО человека, под его ситом: у работающего
        // по трём конторам сутки выглядят иначе, чем в среднем по ленте. Карта
        // ещё не пришла или ровная — расширение возьмёт запасное окно.
        bestWindow(useShift.getState().hours),
      ),
    );
  }, [rows]);
}

/**
 * Отдать расширению профили площадок и увезти на сервер его отчёты о промахах.
 *
 * Обе половины живут здесь, потому что обе — это сеть, а расширению сеть не
 * положена. Один раз за жизнь вкладки: селекторы правят раз в неделю, а не раз
 * в минуту, и человек, открывший ленту утром, получит новые при первом же
 * обновлении страницы.
 */
export function useProfiles(): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    void (async () => {
      tellProfiles(await fetchProfiles((url) => fetch(url), apiUrl('/v1/profiles')));
    })();

    // Отчёты приходят от расширения тем же мостиком, что и всё остальное, — и
    // отсюда же уезжают на сервер. Расширение о существовании сервера не знает.
    const hear = (event: MessageEvent): void => {
      if (event.source !== window) return;
      // Разбор в `aim/miss.ts`: сообщение в окно кладёт кто угодно, и проверять
      // его состав обязано одно место.
      const misses = parseMisses(event.data);
      if (misses.length > 0) report(misses, sendMiss);
      // Тем же мостиком приезжает и главное число приёмки: сколько секунд
      // прошло от нажатия в ленте до «остался один клик».
      const secs = parseSecs(event.data);
      if (secs.length > 0) reportSecs(secs, sendSec);
      const shelf = parseShelf(event.data);
      if (shelf !== null) {
        if (!shelf.pulse) notePages(shelf.clips);
        clipsToDesk(shelf.clips);
      }
      const drive = parseDrive(event.data);
      if (drive !== null) useDrive.getState().note(drive);
    };
    window.addEventListener('message', hear);
    return () => window.removeEventListener('message', hear);
  }, []);
}

const siteAt = new Map<string, number>();
const siteVal = new Map<string, string>();
const SITE_EVERY_MS = 400;

/**
 * Клип страницы конторы → кэф стола: только вилка на полке или открытая
 * карточка, только узнанные матч и сторона. Чужой кэф не пишем.
 * Ушла из кадра — стол её держит, и касса кормит его дальше: строка умерла,
 * а вторую ногу ещё ставят.
 * То же число не чаще 400 мс; новое — сразу, иначе стол отстаёт от кассы.
 */
export function clipsToDesk(clips: readonly PageClip[], now = Date.now()): number {
  const screen = useScreen.getState();
  const ids = new Set([...clipIds(), ...(screen.open !== null ? [screen.open] : [])]);
  let taken = 0;
  for (const id of ids) {
    const fork = screen.forks.get(id) ?? screen.ghosts.get(id)?.fork;
    if (fork === undefined) continue;
    const rows = [];
    for (const clip of clips) {
      if (clip.state !== 'match' && clip.state !== 'moved' && clip.suspended !== true) continue;
      // Дамп лобби (trusted:false): имена есть где-то на главной, число — чужое.
      if (clip.trusted === false) continue;
      // Ячейка уже своя. Имена на SPA часто в другом написании — не режем.
      if (clip.trusted !== true && (!clip.whoHit || !clip.sideHit)) continue;
      if (clip.closest === null && clip.suspended !== true) continue;
      if (!(fork.legs ?? []).some((leg) => leg.s === clip.venue)) continue;
      const key = `${id}|${clip.venue}`;
      const stamp = `${clip.closest ?? ''}|${clip.suspended === true ? '1' : '0'}`;
      const same = siteVal.get(key) === stamp;
      if (same && now - (siteAt.get(key) ?? 0) < SITE_EVERY_MS) continue;
      siteAt.set(key, now);
      siteVal.set(key, stamp);
      rows.push({
        venue: clip.venue,
        closest: clip.closest === null ? null : Number(clip.closest),
        state: clip.state,
        trusted: true,
        ...(clip.suspended === true ? { suspended: true } : {}),
      });
    }
    if (rows.length === 0) continue;
    rememberSite(id, fork, rows, now);
    taken += rows.length;
  }
  return taken;
}

const post = (path: string, body: unknown): Promise<unknown> =>
  fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });

const sendMiss = (body: AimMiss): Promise<unknown> => post('/v1/miss', body);
const sendSec = (body: ReadySec): Promise<unknown> => post('/v1/secs', body);
